import React, { useMemo } from "react";

export type TableColumn<T> = {
  label: string;
  render: (item: T) => React.ReactNode;
};

type DataTableProps<T> = {
  title: string;
  columns: TableColumn<T>[];
  rows: T[];
  total: number;
  page: number;
  take: number;
  onPageChange: (page: number) => void;
};

export const DataTable = <T extends { id?: number }>({
  title,
  columns,
  rows,
  total,
  page,
  take,
  onPageChange,
}: DataTableProps<T>) => {
  const skip = (page - 1) * take;

  const paginationText = useMemo(() => {
    if (total === 0) {
      return "0 resultados";
    }
    const start = skip + 1;
    const end = Math.min(skip + take, total);
    return `${start} - ${end} de ${total}`;
  }, [skip, take, total]);

  return (
    <div className="card shadow-sm">
      <div className="card-body">
        <h5 className="card-title">{title}</h5>
        <div className="table-responsive">
          <table className="table table-sm align-middle">
            <thead>
              <tr>
                {columns.map((column) => (
                  <th key={column.label}>{column.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((item, index) => (
                <tr key={item.id ?? index}>
                  {columns.map((column) => (
                    <td key={column.label}>{column.render(item)}</td>
                  ))}
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={columns.length} className="text-center">
                    Nenhum registro encontrado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="d-flex justify-content-between align-items-center">
          <small className="text-muted">{paginationText}</small>
          <div className="btn-group">
            <button
              className="btn btn-outline-secondary btn-sm"
              disabled={page <= 1}
              onClick={() => onPageChange(Math.max(1, page - 1))}
            >
              Anterior
            </button>
            <button
              className="btn btn-outline-secondary btn-sm"
              disabled={skip + take >= total}
              onClick={() => onPageChange(page + 1)}
            >
              Proximo
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
